import React, { useEffect, useMemo, useState } from 'react';
import '../css/accounting.css';

const formatCurrency = (value) => {
  const number = Number(value || 0);
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(number);
};

const getCurrentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

function Profile() {
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [income, setIncome] = useState({ monthly_income: 0, monthly_bonus: 0 });
  const [accounts, setAccounts] = useState([]);
  const [credits, setCredits] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [transactions, setTransactions] = useState([]);

  const [inviteLink, setInviteLink] = useState('');
  const [inviteStatusMessage, setInviteStatusMessage] = useState('');
  const [inviteErrorMessage, setInviteErrorMessage] = useState('');

  const user = localStorage.getItem('loggedInUsername') || '';

  useEffect(() => {
    const loadProfile = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token || !user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setErrorMessage('');

        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        };

        const [incomeRes, accountsRes, creditsRes, expensesRes, transactionsRes] = await Promise.all([
          fetch('/api/accounting/income', { method: 'POST', headers, body: JSON.stringify({ user }) }),
          fetch('/api/accounting/accounts', { method: 'POST', headers, body: JSON.stringify({ user }) }),
          fetch('/api/accounting/credits', { method: 'POST', headers, body: JSON.stringify({ user }) }),
          fetch('/api/accounting/expenses', { method: 'POST', headers, body: JSON.stringify({ user }) }),
          fetch('/api/accounting/transactions', { method: 'POST', headers, body: JSON.stringify({ user, limit: 5000 }) })
        ]);

        if (!incomeRes.ok || !accountsRes.ok || !creditsRes.ok || !expensesRes.ok || !transactionsRes.ok) {
          throw new Error('Failed to load profile data');
        }

        const [incomeData, accountsData, creditsData, expensesData, transactionsData] = await Promise.all([
          incomeRes.json(),
          accountsRes.json(),
          creditsRes.json(),
          expensesRes.json(),
          transactionsRes.json()
        ]);

        setIncome(incomeData || { monthly_income: 0, monthly_bonus: 0 });
        setAccounts(Array.isArray(accountsData) ? accountsData : []);
        setCredits(Array.isArray(creditsData) ? creditsData : []);
        setExpenses(Array.isArray(expensesData) ? expensesData : []);
        setTransactions(Array.isArray(transactionsData) ? transactionsData : []);
      } catch (error) {
        setErrorMessage('Unable to load your profile insights right now.');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [user]);

  const totals = useMemo(() => {
    const monthlyIncome = Number(income.monthly_income || 0);
    const monthlyBonus = Number(income.monthly_bonus || 0);
    const monthlyIncomeTotal = monthlyIncome + monthlyBonus;

    const totalAccountsBalance = accounts.reduce((sum, account) => sum + Number(account.balance || 0), 0);
    const totalCreditBalance = credits.reduce((sum, credit) => sum + Number(credit.balance || 0), 0);
    const totalCreditLimit = credits.reduce((sum, credit) => sum + Number(credit.credit_limit || 0), 0);

    const recurringExpenses = expenses
      .filter((expense) => Boolean(expense.recurring))
      .reduce((sum, expense) => sum + Number(expense.amount || 0), 0);

    const availableAfterRecurring = monthlyIncomeTotal - recurringExpenses;

    const monthKey = getCurrentMonthKey();
    const monthTransactions = transactions.filter((row) => row.statement_month === monthKey);
    const monthDebit = monthTransactions
      .filter((row) => row.entry_type === 'debit')
      .reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const monthCredit = monthTransactions
      .filter((row) => row.entry_type === 'credit')
      .reduce((sum, row) => sum + Number(row.amount || 0), 0);

    const utilization = totalCreditLimit > 0 ? Math.round((totalCreditBalance / totalCreditLimit) * 100) : 0;

    return {
      monthlyIncomeTotal,
      totalAccountsBalance,
      totalCreditBalance,
      totalCreditLimit,
      recurringExpenses,
      availableAfterRecurring,
      monthTransactionsCount: monthTransactions.length,
      monthDebit,
      monthCredit,
      utilization,
      monthKey
    };
  }, [income, accounts, credits, expenses, transactions]);

  const insights = useMemo(() => {
    const accountNames = accounts.map((account) => account.name).filter(Boolean);
    const topAccount = accountNames.length ? accountNames[0] : 'No accounts yet';

    const avgTransaction = transactions.length
      ? transactions.reduce((sum, row) => sum + Number(row.amount || 0), 0) / transactions.length
      : 0;

    const recurringCount = expenses.filter((expense) => Boolean(expense.recurring)).length;

    return [
      `Current planning month: ${totals.monthKey}`,
      `Recurring expenses tracked: ${recurringCount}`,
      `Average transaction size: ${formatCurrency(avgTransaction)}`,
      `Primary account on file: ${topAccount}`
    ];
  }, [accounts, expenses, totals.monthKey, transactions]);

  const handleCreateInviteLink = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token || !user) {
      setInviteErrorMessage('Please sign in to create invite links.');
      setInviteStatusMessage('');
      return;
    }

    try {
      const response = await fetch('/api/auth/create_invite', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ user })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setInviteErrorMessage(data?.error || 'Unable to create invite link.');
        setInviteStatusMessage('');
        return;
      }

      setInviteLink(data?.invite_link || '');
      setInviteErrorMessage('');
      setInviteStatusMessage('Invite link created. It expires in 5 minutes and can be used once.');
    } catch (error) {
      setInviteErrorMessage('Unable to create invite link.');
      setInviteStatusMessage('');
    }
  };

  const handleCopyInviteLink = async () => {
    if (!inviteLink) {
      return;
    }

    try {
      await navigator.clipboard.writeText(inviteLink);
      setInviteStatusMessage('Invite link copied to clipboard.');
      setInviteErrorMessage('');
    } catch (error) {
      setInviteErrorMessage('Unable to copy invite link.');
    }
  };

  return (
    <div className="lib-page">
      <section className="accounting-page">
        <div className="section-header">
          <div>
            <h2>Profile</h2>
            <p className="section-subtitle">Your accounting pulse, trends, and sharing tools in one place.</p>
          </div>
        </div>

        {loading && <div className="loading-state">Building your profile insights...</div>}
        {!loading && errorMessage && <div className="loading-state">{errorMessage}</div>}

        {!loading && !errorMessage && (
          <>
            <section className="summary-grid">
              <article className="summary-card">
                <p className="summary-label">Monthly income</p>
                <p className="summary-value">{formatCurrency(totals.monthlyIncomeTotal)}</p>
              </article>
              <article className="summary-card">
                <p className="summary-label">Recurring expenses</p>
                <p className="summary-value">{formatCurrency(totals.recurringExpenses)}</p>
              </article>
              <article className="summary-card">
                <p className="summary-label">Available after recurring</p>
                <p className="summary-value">{formatCurrency(totals.availableAfterRecurring)}</p>
              </article>
              <article className="summary-card">
                <p className="summary-label">Total cash accounts</p>
                <p className="summary-value">{formatCurrency(totals.totalAccountsBalance)}</p>
              </article>
            </section>

            <section className="panel-grid">
              <article className="panel panel-secondary">
                <h3>Credit Health</h3>
                <p className="panel-sub">Snapshot of your revolving balances.</p>
                <p className="summary-label">Current balance</p>
                <p className="summary-value">{formatCurrency(totals.totalCreditBalance)}</p>
                <p className="summary-label">Total credit limit</p>
                <p className="summary-value">{formatCurrency(totals.totalCreditLimit)}</p>
                <p className="panel-sub">Utilization: {totals.utilization}%</p>
              </article>

              <article className="panel panel-secondary">
                <h3>Month Activity</h3>
                <p className="panel-sub">Statement month {totals.monthKey}.</p>
                <p className="summary-label">Transactions</p>
                <p className="summary-value">{totals.monthTransactionsCount}</p>
                <p className="panel-sub">Debit total: {formatCurrency(totals.monthDebit)}</p>
                <p className="panel-sub">Credit spend: {formatCurrency(totals.monthCredit)}</p>
              </article>
            </section>

            <section className="panel panel-secondary">
              <h3>Insights</h3>
              <p className="panel-sub">Quick observations based on your current data.</p>
              <div className="form-grid">
                {insights.map((item) => (
                  <div className="badge" key={item}>{item}</div>
                ))}
              </div>
            </section>

            <section className="panel panel-secondary">
              <div className="section-header">
                <div>
                  <h3>Inviting friends</h3>
                  <p className="panel-sub">Create a one-time invite link that expires in 5 minutes.</p>
                </div>
              </div>
              <form className="form-grid" onSubmit={(event) => event.preventDefault()}>
                <div className="form-row">
                  <button className="login-button" type="button" onClick={handleCreateInviteLink}>
                    Create invite link
                  </button>
                </div>
                <div className="form-row">
                  <label>
                    Invite link
                    <input
                      type="text"
                      value={inviteLink}
                      readOnly
                      placeholder="Create an invite link to share"
                    />
                  </label>
                </div>
                <div className="form-row">
                  <button className="login-button" type="button" onClick={handleCopyInviteLink} disabled={!inviteLink}>
                    Copy invite link
                  </button>
                </div>
                {inviteStatusMessage && <p className="panel-sub">{inviteStatusMessage}</p>}
                {inviteErrorMessage && <p className="panel-sub">{inviteErrorMessage}</p>}
              </form>
            </section>
          </>
        )}
      </section>
    </div>
  );
}

export default Profile;
